import { Rgb, Scene, WallpaperStyleInfo, darken, lighten, luminance, prng, rgba, share } from '../wallpaper-core';

export const INFO: WallpaperStyleInfo = {
  key: 'bauhaus', label: 'Bauhaus',
  blurb: 'Flat circles, quarter arcs and triangles on a strict grid, the Bauhaus poster look',
  params: [ share( 'grid', 'Grid', 40 ), share( 'fill', 'Fill', 60 ) ],
};

const TAU = Math.PI * 2;
/** a quarter turn: every corner, side and arc in the vocabulary is a multiple of it */
const Q = Math.PI / 2;

/** the corner codes 0..3 run clockwise from the top left (the side codes do too, from the top) */
const CORNER_X = [ 0, 1, 1, 0 ];
const CORNER_Y = [ 0, 0, 1, 1 ];

/** how far apart in lightness a flat colour and what it sits on have to be to tell each other apart */
const MIN_STEP = 0.18;

/** a cell the seed handed to a run: the corner its quarter disc turns around, and the run it belongs to */
interface Chain { corner: number; run: number; }

/**
 * Bauhaus: the box is cut into a strict grid of square cells and each cell gets one flat shape of
 * a small vocabulary (a quarter disc, a half disc, a disc, a ring, a half square triangle, a lens,
 * a square, or bare ground). Every fill is hard edged and every path is a vector at full size, so
 * there is nothing to band or to look glassy. The seed also plants a few runs of adjacent quarter
 * discs whose arcs meet tangentially: a straight run draws one S curve, a two by two block draws
 * one circle, which is the motif that says Bauhaus rather than "shapes on a grid".
 */
export function draw( { ctx, w, h, box, colors, rand, p, seed }: Scene ): void {
  // the first tile is the ground of every cell, so the poster starts as one flat sheet of it
  const ground = colors[ 0 ];
  ctx.fillStyle = rgba( ground, 1 );
  ctx.fillRect( 0, 0, w, h );

  // Grid: 12 columns across the box at 0, 6 at 100; the cell is square and the rows follow it
  const cols = Math.round( 12 - p[ 0 ] * 6 );
  const cell = box.w / cols;
  // the columns sit on the box edges; the rows are placed so the top and bottom ones are cut by
  // the same amount, and always show more than half a cell, so they bleed off the sheet the way a
  // printed poster does instead of leaving a sliver
  const ox = box.x;
  const spill = box.h / cell - Math.floor( box.h / cell );
  const oy = box.y - ( 1 - spill ) / 2 * cell;
  // one cell past every edge, so an ultrawide canvas and the portrait crop are covered as well
  const c0 = Math.floor( -ox / cell ) - 1;
  const c1 = Math.ceil( ( w - ox ) / cell ) + 1;
  const r0 = Math.floor( -oy / cell ) - 1;
  const r1 = Math.ceil( ( h - oy ) / cell ) + 1;

  // the tiles after the first are the inks, walked in order cell by cell along the rows; a single
  // ink is the classic two colour poster, so only a palette of one needs a tone invented for it
  const inks = colors.length > 1 ? colors.slice( 1 ) : [ luminance( ground ) < 0.5 ? lighten( ground, 0.65 ) : darken( ground, 0.65 ) ];
  // the ink is a function of the CELL, not a running counter: a counter walked more cells before
  // it reached the middle of a wider canvas, so the colours there changed with the canvas
  const inkAt = ( r: number, c: number ): Rgb => inks[ ( ( c + r * 3 ) % inks.length + inks.length ) % inks.length ];

  // the rows and columns that are whole on the sheet: a run is planted among them, so the motif
  // never arrives as a fragment at an edge
  // measured on the DESIGN BOX, never on the canvas: a wider canvas then plants its runs in the
  // same cells, so an ultrawide is the same poster with more of it at the sides (owner, 2026-09-26)
  const ri = Math.ceil( ( box.y - oy ) / cell ), rn = Math.max( 1, Math.floor( ( box.y + box.h - oy ) / cell ) - ri - 1 );
  const ci = Math.ceil( ( box.x - ox ) / cell ), cn = Math.max( 1, Math.floor( ( box.x + box.w - ox ) / cell ) - ci - 1 );

  // two or three runs, planted before anything is drawn so their cells override the seed's choice
  const chains = new Map<number, Chain>();
  const keyOf = ( r: number, c: number ) => ( r + 512 ) * 4096 + c + 512;
  const runs = 2 + Math.floor( rand() * 2 );
  for ( let i = 0; i < runs; i++ ) {
    // the first run is always the S curve and the second always the circle, so both halves of the
    // motif are in every picture; a third run takes whichever the seed likes
    const roll = rand();
    const circle = i === 1 || ( i > 1 && roll < 0.45 );
    const across = rand() < 0.55;
    const len = 3 + Math.floor( rand() * 3 );
    // the two corners that alternate along the run: their arcs share an endpoint and a tangent
    // there, so the quarters read as one wave instead of a row of separate pies
    const pair = across
      ? ( rand() < 0.5 ? [ 0, 1 ] : [ 3, 2 ] )
      : ( rand() < 0.5 ? [ 0, 3 ] : [ 1, 2 ] );
    // a few tries for a place no earlier run has taken: a run that lost cells to another would
    // break its curve and wear two colours
    for ( let t = 0; t < 6; t++ ) {
      const r = ri + Math.floor( rand() * rn );
      const c = ci + Math.floor( rand() * cn );
      // four quarters all turning around a block's middle corner add up to one full circle
      const cells: number[][] = circle
        ? [ [ r, c, 2 ], [ r, c + 1, 3 ], [ r + 1, c, 1 ], [ r + 1, c + 1, 0 ] ]
        : Array.from( { length: len }, ( _, k ) => [ across ? r : r + k, across ? c + k : c, pair[ k % 2 ] ] );
      if ( cells.some( q => chains.has( keyOf( q[ 0 ], q[ 1 ] ) ) ) ) continue;
      for ( const q of cells ) chains.set( keyOf( q[ 0 ], q[ 1 ] ), { corner: q[ 2 ], run: i } );
      break;
    }
  }
  // a run is one ribbon, so it keeps the ink of the cell where the walk first meets it
  const runInk = new Map<number, Rgb>();

  // Fill: a sparse poster at 0, every cell carrying a shape at 100
  const density = 0.2 + p[ 1 ] * 0.8;
  // two shapes that meet on a cell edge each cover half of that pixel row, which would let the
  // ground show through as a hairline between them; a stroke of the shape's own colour closes it
  ctx.lineWidth = Math.max( 0.6, 0.6 * box.w / 1920 );
  ctx.lineJoin = 'round';

  for ( let r = r0; r < r1; r++ ) {
    for ( let c = c0; c < c1; c++ ) {
      // each cell draws from its own generator, so its shape never depends on its neighbours
      const g = prng( ( Math.imul( seed, 2654435761 ) + r * 1000003 + c * 7919 ) >>> 0 );
      const shown = g() < density;
      const blocked = g() < 0.2;
      const kindRoll = g();
      const turn = Math.floor( g() * 4 );
      const chain = chains.get( keyOf( r, c ) );
      if ( !chain && !shown ) continue;
      const x = ox + c * cell;
      const y = oy + r * cell;
      if ( x > w || y > h || x + cell < 0 || y + cell < 0 ) continue;

      if ( chain ) {
        let ink = runInk.get( chain.run );
        if ( !ink ) {
          ink = readable( inkAt( r, c ), ground );
          runInk.set( chain.run, ink );
        }
        ctx.fillStyle = ctx.strokeStyle = rgba( ink, 1 );
        paint( ctx, 0, x, y, cell, chain.corner );
        continue;
      }

      const kind = kindOf( kindRoll );
      if ( !blocked ) {
        ctx.fillStyle = ctx.strokeStyle = rgba( readable( inkAt( r, c ), ground ), 1 );
        paint( ctx, kind, x, y, cell, turn );
        continue;
      }
      // the second colour block fills the whole cell, so the shape on it is a figure on a field
      const seat = readable( inkAt( r, c ), ground );
      ctx.fillStyle = ctx.strokeStyle = rgba( seat, 1 );
      // pushing a tile that is already close to the block would only grey it, so the shape is
      // punched out in the ground instead, which the block is guaranteed to clear
      const walked = inkAt( r, c + 1 );
      if ( Math.abs( luminance( walked ) - luminance( seat ) ) < MIN_STEP ) {
        // one path, the block with the shape as its hole: drawing the ground over the block
        // instead would leave the block's own antialiased edge as a hairline around the cell
        paint( ctx, kind, x, y, cell, turn, true );
        continue;
      }
      paint( ctx, -1, x, y, cell, turn );
      ctx.fillStyle = ctx.strokeStyle = rgba( walked, 1 );
      paint( ctx, kind, x, y, cell, turn );
    }
  }
}

/** the vocabulary by weight: the quarter disc leads because it is what the eye reads as Bauhaus */
const WEIGHTS = [ 0.3, 0.15, 0.14, 0.14, 0.09, 0.09, 0.09 ];

function kindOf( roll: number ): number {
  let acc = 0;
  for ( let i = 0; i < WEIGHTS.length; i++ ) {
    acc += WEIGHTS[ i ];
    if ( roll < acc ) return i;
  }
  return WEIGHTS.length - 1;
}

/**
 * One flat shape inside the cell at (x, y) of side s; `turn` is the corner or side it sits on,
 * `kind` -1 the bare cell. With `hole` the cell is traced first and the shape is cut out of it
 * under the even odd rule, so the two share one edge instead of one covering the other.
 */
function paint( ctx: CanvasRenderingContext2D, kind: number, x: number, y: number, s: number, turn: number, hole = false ): void {
  const cx = x + s / 2, cy = y + s / 2;
  const k = ( ( turn % 4 ) + 4 ) % 4;
  ctx.beginPath();
  if ( hole || kind < 0 ) ctx.rect( x, y, s, s );
  switch ( kind ) {
    case 0: {
      // the quarter disc: radius of a whole cell, so it meets both neighbouring cells at a corner
      const qx = x + CORNER_X[ k ] * s, qy = y + CORNER_Y[ k ] * s;
      ctx.moveTo( qx, qy );
      ctx.arc( qx, qy, s, k * Q, k * Q + Q );
      break;
    }
    case 1:
      ctx.moveTo( cx + s * 0.46, cy );
      ctx.arc( cx, cy, s * 0.46, 0, TAU );
      break;
    case 2: {
      // the half disc stands on one side of the cell and faces in
      const hx = k === 1 ? x + s : k === 3 ? x : cx;
      const hy = k === 0 ? y : k === 2 ? y + s : cy;
      ctx.moveTo( hx + Math.cos( k * Q ) * s * 0.5, hy + Math.sin( k * Q ) * s * 0.5 );
      ctx.arc( hx, hy, s * 0.5, k * Q, k * Q + Math.PI );
      break;
    }
    case 3: {
      // the half square: the right angle at one corner, the hypotenuse on the diagonal
      const a = ( k + 1 ) % 4, b = ( k + 3 ) % 4;
      ctx.moveTo( x + CORNER_X[ k ] * s, y + CORNER_Y[ k ] * s );
      ctx.lineTo( x + CORNER_X[ a ] * s, y + CORNER_Y[ a ] * s );
      ctx.lineTo( x + CORNER_X[ b ] * s, y + CORNER_Y[ b ] * s );
      break;
    }
    case 4:
      // the lens: two cell wide arcs from opposite corners, the leaf of every Bauhaus alphabet
      if ( k % 2 === 0 ) {
        ctx.moveTo( x + s, y );
        ctx.arc( x, y, s, 0, Q );
        ctx.arc( x + s, y + s, s, Math.PI, Math.PI + Q );
      } else {
        ctx.moveTo( x + s, y + s );
        ctx.arc( x + s, y, s, Q, Math.PI );
        ctx.arc( x, y + s, s, 3 * Q, TAU );
      }
      break;
    case 5:
      // the ring: the inner circle runs the other way, which leaves the hole under the nonzero rule
      ctx.moveTo( cx + s * 0.46, cy );
      ctx.arc( cx, cy, s * 0.46, 0, TAU );
      ctx.moveTo( cx + s * 0.28, cy );
      ctx.arc( cx, cy, s * 0.28, 0, TAU, true );
      break;
    case 6:
      ctx.rect( cx - s * 0.31, cy - s * 0.31, s * 0.62, s * 0.62 );
      break;
    default:
      // kind -1: the bare block, already traced above
      break;
  }
  ctx.closePath();
  ctx.fill( hole ? 'evenodd' : 'nonzero' );
  // a cut out cell is never stroked: the hole's edge and the cell's cancel each other in the fill,
  // and a stroke would paint that cancelled line back in as a rim around the cell
  if ( !hole ) ctx.stroke();
}

/**
 * A flat ink has no shading to rescue it, so a tile too close in lightness to what it sits on is
 * pushed away from it: up on a dark seat, down on a light one. It is moved further than the
 * distance that triggered the move, so a palette of near neighbours does not come out as a set of
 * shapes all sitting exactly on the threshold. Lightening and darkening are linear in luminance,
 * so the step lands where it is asked to.
 */
function readable( c: Rgb, on: Rgb ): Rgb {
  const lo = luminance( on ), lc = luminance( c );
  if ( Math.abs( lc - lo ) >= MIN_STEP ) return c;
  if ( lo < 0.5 ) {
    const t = ( Math.min( 0.97, lo + 0.24 ) - lc ) / Math.max( 0.001, 1 - lc );
    return lighten( c, Math.min( 1, Math.max( 0, t ) ) );
  }
  const t = 1 - Math.max( 0.03, lo - 0.24 ) / Math.max( 0.001, lc );
  return darken( c, Math.min( 1, Math.max( 0, t ) ) );
}
